import SrDocumentDocCreate from './SrDocumentDocCreate'
import SrDocumentDocDelete from './SrDocumentDocDelete'
import SrDocumentDocUpdate from './SrDocumentDocUpdate'
import { fetchSrDocumentDocs } from 'actions/srDocuments/srDocumentDocActions'
import EmptyList from 'components/common/EmptyList'
import Modal from 'components/common/Modal'
import Section from 'components/common/Section'
import Table from 'components/common/tables/Table'
import { useAuth } from 'hooks/useAuth.js'
import useModal from 'hooks/useModal'
import _ from 'lodash'
import { MDBIcon } from 'mdbreact'
import React, { useEffect, useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { isUserNotReadOnlyForSiteReadiness } from 'utils/authUtils'
import { requestSrDocumentDoc } from 'utils/downloadUtils'

const newHeader = 'New File'
const editHeader = 'Edit File'
const deleteHeader = 'Delete File'

const SrDocumentDocList = ({ srDocument }) => {
  //// HOOKS.
  const dispatch = useDispatch()
  const { user } = useAuth() ?? {}
  const { user_level, is_admin } = user ?? {}
  const {
    modalOpen,
    headerText,
    setHeaderText,
    formId,
    footer,
    setFooter,
    actions,
    setActions,
    selected,
    setSelected,
    params,
    setParams,
    setModalState,
  } = useModal()

  //// GLOBAL STATE.
  const { docsAvailable, lastAction } = useSelector(
    (state) => state.srDocumentDocs
  )

  //// LOCAL STATE.
  const [tableData, setTableData] = useState({
    columns: [
      {
        label: 'Date Stored (UTC)',
        field: 'created_at',
        date: true,
        sort: 'desc',
      },
      {
        label: 'File',
        field: 'filename',
        sort: 'desc',
      },
      {
        label: 'Type',
        field: 'sr_document_doc_type.name',
        sort: 'asc',
      },
      {
        label: 'Comment',
        field: 'comment',
        sort: 'asc',
      },
      {
        label: '',
        field: 'buttons',
        sort: 'disabled',
        minimal: 'lg',
      },
    ],
  })

  //// LIFECYCLE HELPERS.

  //// MEMOS.

  //// EFFECTS.
  useEffect(() => {
    dispatch(fetchSrDocumentDocs({ sr_document_id: srDocument.id }))
  }, [dispatch, srDocument.id])

  //// RENDER VARS & HELPERS.
  const toggleModal = useCallback(
    (e, type, item) => {
      if (e) e.preventDefault()

      if (type === 'new') {
        setHeaderText(newHeader)
        setFooter(true)
        setActions([
          {
            text: 'Submit',
            color: 'primary',
            additionalProps: {
              form: formId,
              type: 'submit',
            },
          },
        ])
      } else if (type === 'edit') {
        setHeaderText(editHeader)
        setFooter(true)
        setActions([
          {
            text: 'Update',
            color: 'primary',
            additionalProps: {
              form: formId,
              type: 'submit',
            },
          },
        ])
        setSelected(item)
      } else if (type === 'delete') {
        setHeaderText(deleteHeader)
        setFooter(true)
        setActions([
          {
            text: 'Delete',
            color: 'danger',
            additionalProps: {
              form: formId,
              type: 'submit',
            },
          },
        ])
        setSelected(item)
      }
      if (type) {
        setModalState(true)
      } else {
        setModalState(false)
      }
    },
    [formId, setActions, setFooter, setHeaderText, setModalState, setSelected]
  )

  const fetchTableLink = useCallback(
    (type, item) => {
      switch (type) {
        case 'history':
          return `/sr-documents/${srDocument.id}/doc-types/${item.sr_document_doc_type.id}`
        default:
          return null
      }
    },
    [srDocument.id]
  )

  const handleDocDownload = useCallback((e, item) => {
    e.preventDefault()
    requestSrDocumentDoc(item.id, item.filename)
  }, [])

  const sectionHeaderMenu = {
    order: 2,
    show: srDocument.current_state === 0 ? true : false,
    linkItems: [
      {
        name: (
          <>
            <MDBIcon icon="fas fa-plus" /> Add File
          </>
        ),
        exact: true,
        additionalProps: {
          active: true,
          onClick: (e) => toggleModal(e, 'new'),
          className: 'btn-sm',
        },
      },
    ],
  }

  //// RENDER.
  return (
    <>
      <Section
        header="Site Readiness Document Files"
        description={
          srDocument.current_state !== 0
            ? `Site readiness document file cannot be updated or added when the site readiness document is ${srDocument.current_state_text.toLowerCase()}`
            : 'View, add, and edit site readiness document file'
        }
        menu={
          isUserNotReadOnlyForSiteReadiness(user_level)
            ? sectionHeaderMenu
            : null
        }
      >
        {_.size(docsAvailable) ? (
          <Table
            section="Site Readiness Document File"
            tableData={tableData}
            setTableData={setTableData}
            rowData={docsAvailable}
            sort={['created_at', 'desc']}
            fetchTableLink={fetchTableLink}
            toggleModal={toggleModal}
            downloadFile={handleDocDownload}
            updatedState={lastAction}
            downloadableCSV
            downloadButton
            showHistoryButton
            editButton={
              srDocument.current_state === 0 &&
              isUserNotReadOnlyForSiteReadiness(user_level)
            }
            deleteButton={is_admin}
          />
        ) : _.isEmpty(docsAvailable) && srDocument.current_state === 0 ? (
          <EmptyList
            name="file"
            namePlural="files"
            userRole={user_level}
            createElement={
              <span className="link" onClick={(e) => toggleModal(e, 'new')}>
                Create
              </span>
            }
          />
        ) : _.isEmpty(docsAvailable) ? (
          <p>
            No site readiness document files were added to this site readiness
            document while open.
          </p>
        ) : (
          void 0
        )}
      </Section>

      <Modal
        isOpen={modalOpen}
        toggle={toggleModal}
        headerText={headerText}
        includeFooter={footer}
        actionButtons={actions}
      >
        {headerText === newHeader ? (
          <SrDocumentDocCreate
            formId={formId}
            srDocumentId={srDocument.id}
            params={params}
            setParams={setParams}
            closeModal={() => setModalState(false)}
          />
        ) : headerText === editHeader ? (
          <SrDocumentDocUpdate
            formId={formId}
            srDocumentId={srDocument.id}
            params={params}
            setParams={setParams}
            selected={selected}
            setSelected={setSelected}
            closeModal={() => setModalState(false)}
          />
        ) : headerText === deleteHeader ? (
          <SrDocumentDocDelete
            formId={formId}
            selected={selected}
            closeModal={() => setModalState(false)}
          />
        ) : (
          void 0
        )}
      </Modal>
    </>
  )
}

export default SrDocumentDocList
